"use client"

import { useState, useEffect } from "react"
import { MapPin, Loader2, AlertCircle } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { getCurrentUser } from "@/lib/auth"

interface LocationPermissionModalProps {
  onSuccess: () => void
}

export default function LocationPermissionModal({ onSuccess }: LocationPermissionModalProps) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [attempt, setAttempt] = useState(0)

  const requestLocation = async () => {
    setLoading(true)
    setError("")

    try {
      console.log("[v0] [Location Modal] Requesting GPS position with high accuracy...")

      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true, // Force high accuracy GPS for precise location
          timeout: 20000,
          maximumAge: 5000, // Reduced from 60000 to get fresher position
        })
      })

      console.log("[v0] [Location Modal] GPS position acquired:", {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
      })

      const user = getCurrentUser()
      if (user) {
        console.log("[v0] [Location Modal] Updating user location via API...")

        // Update user location via API
        const response = await fetch("/api/location/update", {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            userId: user.id,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          }),
        })

        if (response.ok) {
          console.log("[v0] [Location Modal] Location saved successfully")
          onSuccess()
        } else {
          throw new Error("Failed to save location")
        }
      }
    } catch (err: any) {
      console.error("[v0] [Location Modal] Error getting location:", err)
      setAttempt((prev) => prev + 1)
      if (err.code === 1) {
        setError("Location permission denied. Please enable location in your browser settings.")
      } else if (err.code === 2) {
        setError("Location unavailable. Please check your device settings.")
      } else if (err.code === 3) {
        setError("Location request timed out. Please try again.")
      } else {
        setError("Failed to get location. Please try again.")
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (attempt > 0 && attempt < 3 && error && !loading) {
      const timer = setTimeout(() => {
        requestLocation()
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [attempt, error, loading])

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-md shadow-2xl">
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto w-16 h-16 bg-cyan-500 rounded-full flex items-center justify-center">
            <MapPin className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl">Enable Location</CardTitle>
          <CardDescription className="text-base">
            Bubble It needs your location to show you nearby groups and users
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-red-700">{error}</p>
                {attempt < 3 && <p className="text-xs text-red-600 mt-1">Retrying... (Attempt {attempt}/3)</p>}
              </div>
            </div>
          )}

          <Button
            onClick={requestLocation}
            disabled={loading}
            className="w-full h-12 bg-cyan-500 hover:bg-cyan-600 text-base"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Getting location...
              </>
            ) : (
              <>
                <MapPin className="w-5 h-5 mr-2" />
                Enable Location
              </>
            )}
          </Button>

          <p className="text-xs text-center text-muted-foreground">
            Your location is only used to show nearby content. We respect your privacy.
          </p>
        </CardContent>
      </Card>
    </div>
  )
}
